'use client'

import React from 'react'

import { stegaClean } from 'next-sanity'

import { Icon } from '@/app/components/Icon'
import { Link, LinkProps } from '@/app/components/Link'
import { useButtonOverride } from '@/app/providers/SegmentOverridesProvider'
import { buildTrackingData, trackElementClick, TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { Button as ButtonRaw, Icon as IconRaw } from '@/sanity.types'
import type { DeepRequiredNullable } from '@/types/utils'

function isLinkElement(element: React.ReactElement): element is React.ReactElement<LinkProps> {
  return element.type === Link
}

type ButtonWithNulls = DeepRequiredNullable<ButtonRaw>
type IconWithNulls = DeepRequiredNullable<IconRaw>

type IconWithDeref = IconWithNulls

export type ButtonWithDeref = Omit<ButtonWithNulls, 'link' | 'icon' | 'alignment'> & {
  link?: LinkProps['link'] | null
  icon?: IconWithDeref | null
  /** Article buttons only: set in the blog post editor */
  alignment?: ButtonWithNulls['alignment']
}

type ButtonPropsBase = {
  button: ButtonWithDeref
  tracking: TrackingLocation
  className?: string
  ignoreVariantStyles?: boolean
  themeMode?: 'light' | 'dark'
  variantMapping?: Record<string, string>
}

type ButtonPropsWithAsChild = ButtonPropsBase & {
  asChild: true
  children: React.ReactElement<LinkProps>
}

type ButtonPropsWithoutAsChild = ButtonPropsBase &
  React.ComponentProps<'button'> & {
    asChild?: false
    children?: React.ReactNode
  }

export type ButtonProps = ButtonPropsWithAsChild | ButtonPropsWithoutAsChild

/**
 * Generate button class name from variant and size using theme CSS classes.
 * Maps to classes defined in frontend/app/tailwind/theme.css
 *
 * @example
 * getButtonClassName('primary', 'md') // 'btn-primary'
 * getButtonClassName('primary', 'sm') // 'btn-primarySm'
 * getButtonClassName('secondaryDark', 'md') // 'btn-secondaryDark'
 * getButtonClassName('navbarLink', 'md') // 'btn-navbarLink' (no size variant)
 */
function getButtonClassName(variant: string, size: string): string {
  // Fixed-size variants (no size variations in theme.css)
  const fixedSizeVariants = [
    'navbarLink',
    'navbarLinkDark',
    'navbarCta',
    'navbarCtaDark',
    'tab',
    'tabDark',
  ]
  if (fixedSizeVariants.includes(variant)) {
    return `btn-${variant}`
  }

  // For 'md' size, use base class name; for 'sm', append 'Sm' suffix
  const sizeSuffix = size === 'sm' ? 'Sm' : ''
  return `btn-${variant}${sizeSuffix}`
}

type ButtonSize = 'md' | 'sm'
type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

// Pre-generate all possible responsive size class combinations for Tailwind scanning
// These override the size-specific properties (padding, font-size) at each breakpoint
const responsiveSizeClasses = {
  // sm breakpoint
  'sm:md': 'sm:px-buttons-md sm:pt-buttons-md sm:pb-lg sm:size-button-md',
  'sm:sm': 'sm:px-buttons-sm sm:pt-buttons-sm sm:pb-md sm:size-button-sm',
  // md breakpoint
  'md:md': 'md:px-buttons-md md:pt-buttons-md md:pb-lg md:size-button-md',
  'md:sm': 'md:px-buttons-sm md:pdct-buttons-sm md:pb-md md:size-button-sm',
  // lg breakpoint
  'lg:md': 'lg:px-buttons-md lg:pt-buttons-md lg:pb-lg lg:size-button-md',
  'lg:sm': 'lg:px-buttons-sm lg:pt-buttons-sm lg:pb-md lg:size-button-sm',
  // xl breakpoint
  'xl:md': 'xl:px-buttons-md xl:pt-buttons-md xl:pb-lg xl:size-button-md',
  'xl:sm': 'xl:px-buttons-sm xl:pt-buttons-sm xl:pb-md xl:size-button-sm',
  // 2xl breakpoint
  '2xl:md': '2xl:px-buttons-md 2xl:pt-buttons-md 2xl:pb-lg 2xl:size-button-md',
  '2xl:sm': '2xl:px-buttons-sm 2xl:pt-buttons-sm 2xl:pb-md 2xl:size-button-sm',
} as const

/**
 * Generate responsive button size classes for use in PortableText or other contexts.
 * All class combinations are pre-generated for Tailwind's build-time scanning.
 *
 * @example
 * buttonSizeClasses({ lg: 'md' }) // Only apply md size on lg breakpoint
 * buttonSizeClasses({ md: 'md', lg: 'sm' }) // md on md+, sm on lg+
 */
export function buttonSizeClasses(responsive: Partial<Record<Breakpoint, ButtonSize>>): string {
  const classes: string[] = []

  for (const [breakpoint, size] of Object.entries(responsive) as [Breakpoint, ButtonSize][]) {
    const key = `${breakpoint}:${size}` as keyof typeof responsiveSizeClasses
    if (responsiveSizeClasses[key]) {
      classes.push(responsiveSizeClasses[key])
    }
  }

  return classes.join(' ')
}

const iconSizes = {
  md: 15,
  sm: 15,
}

export const Button = function ({
  button,
  tracking,
  className,
  ignoreVariantStyles = false,
  themeMode,
  variantMapping,
  asChild = false,
  children,
  ...buttonProps
}: ButtonProps) {
  // Clean stega encoding from entire Sanity object
  const cleanButton = stegaClean(button)

  // Check for segment-based button override
  const { override, shouldWaitForUser } = useButtonOverride(cleanButton.dataId)

  if (!button) return null

  // Check if button should be hidden (prevents CLS by keeping element in DOM)
  const isHidden = override?.mode === 'hide'

  // Handle click tracking for button elements (not links)
  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const textContent =
      typeof children === 'string' ? children : cleanButton.link?.label || 'Button'
    const trackingData = buildTrackingData('button', textContent, tracking)
    trackElementClick(trackingData)

    // Call original onClick if provided
    if ('onClick' in buttonProps && buttonProps.onClick) {
      buttonProps.onClick(event)
    }
  }

  // Determine final variant - use variantMapping if provided, otherwise use base variant
  let variant = cleanButton.variant || 'primary'

  if (variantMapping && variant in variantMapping) {
    variant = variantMapping[variant] as typeof variant
  }

  const size = cleanButton.size || 'md'
  const iconPlacement = cleanButton.iconPlacement || 'none'
  const hasIcon = iconPlacement !== 'none' && !!cleanButton.icon

  // Determine icon size based on variant
  const isNavbarCta = variant === 'navbarCta' || variant === 'navbarCtaDark'
  const iconSize = isNavbarCta ? 16 : iconSizes[size]

  // Determine gap based on variant
  const gapClass = isNavbarCta ? 'gap-sm' : 'gap-md'

  // Determine icon color and hover effect based on variant
  const isTertiary = variant === 'tertiary' || variant === 'tertiaryDark'
  const iconClassName = cn(
    isTertiary && 'text-neon-200',
    isNavbarCta && 'transition-transform group-hover:translate-x-[1px]',
  )

  const labelClassName = cn(
    hasIcon && isNavbarCta && 'transition-transform group-hover:-translate-x-[1px]',
  )

  const finalClassName = cn(
    !ignoreVariantStyles && getButtonClassName(variant, size),
    !ignoreVariantStyles && gapClass,
    hasIcon && isTertiary && '!px-0',
    'group',
    (shouldWaitForUser || isHidden) && 'invisible pointer-events-none',
    className,
  )

  // Helper to render button content with optional icon
  const renderContent = (label: React.ReactNode) => {
    if (!hasIcon || !cleanButton.icon) return label

    const labelElement = labelClassName ? <span className={labelClassName}>{label}</span> : label

    return (
      <>
        {iconPlacement === 'left' && (
          <Icon icon={cleanButton.icon} size={iconSize} className={iconClassName} />
        )}
        {labelElement}
        {iconPlacement === 'right' && (
          <Icon icon={cleanButton.icon} size={iconSize} className={iconClassName} />
        )}
      </>
    )
  }

  // If button has a link child, render as Link component
  if (cleanButton.link) {
    // Priority: override > children > label > page name > post title > 'Button'
    const linkLabel =
      override?.elementLabel ||
      children ||
      cleanButton.link?.label ||
      cleanButton.link?.internalPage?.name ||
      cleanButton.link?.internalPost?.title ||
      'Button'

    // Apply override URL if present, otherwise use original link
    const linkWithOverride = override?.elementUrl
      ? {
          ...cleanButton.link,
          href: override.elementUrl,
          linkType: 'external' as const,
        }
      : cleanButton.link

    return (
      <Link
        link={linkWithOverride}
        tracking={tracking}
        className={finalClassName}
        data-id={cleanButton.dataId || undefined}
        aria-hidden={isHidden || undefined}
        tabIndex={isHidden ? -1 : undefined}
      >
        {renderContent(linkLabel)}
      </Link>
    )
  }

  // If asChild is true and children is a Link component, render with button styles
  if (asChild && children && React.isValidElement(children) && isLinkElement(children)) {
    return (
      <Link
        link={children.props.link}
        tracking={tracking}
        className={cn(finalClassName, children.props.className)}
        data-id={cleanButton.dataId || undefined}
        aria-hidden={isHidden || undefined}
        tabIndex={isHidden ? -1 : undefined}
      >
        {renderContent(children.props.link?.label || 'Button')}
      </Link>
    )
  }

  // Otherwise render as a button element
  return (
    <button
      type="button"
      className={finalClassName}
      data-id={cleanButton.dataId || undefined}
      aria-hidden={isHidden || undefined}
      tabIndex={isHidden ? -1 : undefined}
      {...buttonProps}
      onClick={handleButtonClick}
    >
      {renderContent(children || 'Button')}
    </button>
  )
}
